/**
 * firebase-context — Firebase Authentication context for the app.
 *
 * Provides:
 *   - currentUserInfo: the signed-in Firebase user object (or null)
 *   - isLoggedIn: boolean shorthand
 *   - isLoading: true while the auth state or user details are being fetched
 *   - userAccess: the user's module-level permission map from Firestore
 *   - signInWithGoogleAccountPopup(): initiates Google OAuth popup flow
 *   - signOutFromApp(): signs the user out of Firebase
 *
 * Auth flow:
 *   1. onAuthStateChanged fires on every page load/tab focus
 *   2. If a user is signed in, fetch their Firestore record (getUserDetails)
 *   3. If the record exists, apply their auth_access permissions
 *   4. If no record yet, create one via /create-user (first-ever sign-in)
 *   5. If signed out, reset all access to false
 */
import { createContext, useContext, useEffect, useRef, useState } from 'react'
import axios from "axios";
import MuiAlert from "../utils/muiAlerts"
import firebaseConfig from '../happy-ears-firebase-config.js'
import { backendOrigin } from '../utils/backendOrigin.js'
import { initializeApp } from 'firebase/app'
import { getAuth, GoogleAuthProvider, onAuthStateChanged, signInWithPopup, signInWithRedirect, signOut } from 'firebase/auth'
import { defaultAccess } from '../components/AdminPanel.js';

const app = initializeApp(firebaseConfig)
const auth = getAuth(app)
const googleProvider = new GoogleAuthProvider()
// Always show the account picker so users can switch accounts
googleProvider.setCustomParameters({ prompt: 'select_account' })

let axiosAuthInterceptorId = null;

const ensureAxiosAuthInterceptor = () => {
    if (axiosAuthInterceptorId !== null) {
        return;
    }

    axiosAuthInterceptorId = axios.interceptors.request.use(async (config) => {
        const requestUrl = config?.url || '';
        const isBackendRequest = typeof requestUrl === 'string' && requestUrl.startsWith(backendOrigin);

        if (!isBackendRequest) {
            return config;
        }

        const currentUser = auth.currentUser;
        if (!currentUser) {
            return config;
        }

        const idToken = await currentUser.getIdToken();
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${idToken}`;
        return config;
    });
}


const FirebaseContext = createContext(null)

export const useFirebase = () => useContext(FirebaseContext)

export const FirebaseProvider = ({ children }) => {

    ensureAxiosAuthInterceptor()

    const [loading, setLoading] = useState(false)
    const [user, setUser] = useState(null)
    const userDetailsInFlightRef = useRef(new Map())
    // userAccess defaults to all-false; gets populated from Firestore on sign-in
    const [userAccess, setUserAccess] = useState(defaultAccess)

    //defaulting userAccess to true for testing purposes
    // const [userAccess, setUserAccess] = useState(Object.fromEntries(Object.keys(defaultAccess).map(key => [key, true])))

    // Helper: POST to /get-user-details — used by both onAuthStateChanged and popup sign-in
    const getUserDetails = (user_uid) => {
        if (userDetailsInFlightRef.current.has(user_uid)) {
            return userDetailsInFlightRef.current.get(user_uid)
        }

        const request = axios.post(`${backendOrigin}/get-user-details`, { user_uid }, { headers: { 'Content-Type': 'application/json' } })
            .finally(() => {
                userDetailsInFlightRef.current.delete(user_uid)
            })

        userDetailsInFlightRef.current.set(user_uid, request)
        return request
    }

    // Subscribe to Firebase Auth state changes for the lifetime of the app.
    // setLoading(true) before any async work, setLoading(false) in all terminal paths.
    useEffect(() => {
        onAuthStateChanged(auth, (user) => {
            setLoading(true)

            if (user) {
                setUser(user)

                getUserDetails(user.uid)
                    .then((res) => {
                        if (res.data.operation === "success" && res.data.info) {
                            res.data.info?.auth_access && setUserAccess(res.data.info.auth_access)
                        }
                        else if (res.data.operation === "success" && !res.data.info) {
                            axios.post(`${backendOrigin}/create-user`, { user_uid: user.uid, user_name: user.displayName, user_email: user.email, user_photo: user.photoURL }, { headers: { 'Content-Type': 'application/json' } })
                                .catch((err) => {
                                    console.error(err)
                                })
                        }
                        else {
                            MuiAlert.fire('Error!!', res.data.message, 'error');
                        }

                        setLoading(false)
                    })
                    .catch((err) => {
                        setLoading(false)

                        console.error(err)
                        MuiAlert.fire('Error!!', err.message, 'error');
                    })
            }
            else {
                setUser(null)
                setUserAccess({
                    audiometry: false,
                    inventory: false,
                    generate_invoice: false,
                    sales_report: false,
                    admin_panel: false,
                    patients: false,
                    service: false,
                })

                setLoading(false)
            }
        })
    }, [])

    // Google popup sign-in — used as primary login method.
    // Falls back to redirect flow if the popup is blocked.
    const signInWithGoogleAccountPopup = async () => {
        try {
            const result = await signInWithPopup(auth, googleProvider)
            const user = result.user
            setUser(user)

            getUserDetails(user.uid)
                .then((res) => {
                    if (res.data.operation !== "success") {
                        axios.post(`${backendOrigin}/create-user`, { user_uid: user.uid, user_name: user.displayName, user_email: user.email, user_photo: user.photoURL }, { headers: { 'Content-Type': 'application/json' } })
                            .catch((err) => {
                                console.error(err)
                            })
                    }
                    else {
                        res.data.info?.auth_access && setUserAccess(res.data.info.auth_access)
                    }
                })
                .catch((err) => {
                    console.error(err)
                    MuiAlert.fire('Error!!', err.message, 'error');
                })
        }
        catch (error) {
            const errorCode = error?.code
            const errorMessage = error?.message
            const email = error?.customData?.email
            console.error('[Auth] sign-in error:', errorCode, errorMessage, email)

            if (errorCode === 'auth/unauthorized-domain') {
                MuiAlert.fire(
                    'Firebase domain not allowed',
                    `Add ${window.location.hostname} to Firebase Authentication > Settings > Authorized domains, then try again.`,
                    'error'
                )
                throw error
            }

            if (errorCode === 'auth/popup-blocked' || errorCode === 'auth/popup-closed-by-user') {
                MuiAlert.fire('Info', 'Popup login was blocked/closed. Redirect login will be used instead.', 'info')
                return signInWithRedirect(auth, googleProvider)
            }

            MuiAlert.fire('Error!!', errorMessage || 'Google sign-in failed.', 'error')
            throw error
        }
    }

    const signOutFromApp = () => {
        return signOut(auth).then(() => {
            // signed out
        }).catch((error) => {
            console.error('[Auth] sign-out error:', error)
        });
    }

    // Expose auth state and actions via context
    return (
        <FirebaseContext.Provider value={{ signInWithGoogleAccountPopup, signOutFromApp, currentUserInfo: user, isLoggedIn: user ? true : false, isLoading: loading, userAccess: userAccess }}>
            {children}
        </FirebaseContext.Provider>
    )
}
